import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import {
  LayoutDashboard, Calendar, FileText, Award, CheckSquare,
  Layers, Globe2, FolderOpen, Settings, ChevronRight,
  ChevronLeft, X, Sparkles, Home, ListTodo, Library, Users
} from 'lucide-react';

const TOUR_STEPS = [
  {
    title: 'Welcome to LexAgenda!',
    description: 'Your complete digital platform for Philippine corporate governance. Let us show you around the key features.',
    icon: Sparkles,
    color: '#00A3A3',
    page: '/dashboard',
  },
  {
    title: 'Home',
    description: 'Your governance command center. See upcoming meetings, pending tasks, draft minutes, reportorial deadlines, and analytics at a glance.',
    icon: Home,
    color: '#00A3A3',
    page: '/dashboard',
  },
  {
    title: 'Meetings',
    description: 'Schedule board meetings, special meetings, and ASMs. View them on the calendar, configure recurring schedules, and send notices to attendees.',
    icon: Calendar,
    color: '#00A3A3',
    page: '/meetings',
  },
  {
    title: 'Minutes & Resolutions',
    description: 'Draft meeting minutes with AI assistance, manage board resolutions with approval tracking, and generate official certificates.',
    icon: FileText,
    color: '#F58A26',
    page: '/minutes',
  },
  {
    title: 'Tasks',
    description: 'Track tasks assigned during meetings. Set deadlines, assign responsible persons, and monitor completion status.',
    icon: ListTodo,
    color: '#00A3A3',
    page: '/action-items',
  },
  {
    title: 'Templates',
    description: 'Build custom document templates with the visual builder, browse the template gallery, and generate documents in seconds.',
    icon: Layers,
    color: '#F58A26',
    page: '/templates',
  },
  {
    title: 'Governance Hub',
    description: 'Share documents for review and approval, conduct polls, manage announcements, and handle stakeholder requests with deadline tracking and comment threads.',
    icon: Globe2,
    color: '#00A3A3',
    page: '/governance',
  },
  {
    title: 'Library',
    description: 'Your centralized document repository. Organize Corporate Books, Meeting Docs, Governance documents, Regulatory Docs, Communications, and other resources. Upload files with classification levels.',
    icon: Library,
    color: '#00A3A3',
    page: '/records',
  },
  {
    title: 'Stakeholders Portal',
    description: 'Share documents, announcements, polls, and meeting summaries with board members and stakeholders. Manage governance requests and collaborative approvals.',
    icon: Users,
    color: '#F58A26',
    page: '/stakeholders',
  },
  {
    title: 'Secretariat Settings',
    description: 'Configure your organization, manage users & roles, set up SMTP email, manage your e-signature, and track board composition.',
    icon: Settings,
    color: '#5A6570',
    page: '/admin',
  },
  {
    title: 'You\'re All Set!',
    description: 'You can retake this tutorial anytime from the Helpdesk. Start by creating your first meeting or exploring the template gallery.',
    icon: Sparkles,
    color: '#00A3A3',
    page: '/dashboard',
  },
];

const OnboardingTutorial = ({ onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [visible, setVisible] = useState(true);
  const navigate = useNavigate();

  const step = TOUR_STEPS[currentStep];
  const Icon = step.icon;
  const isFirst = currentStep === 0;
  const isLast = currentStep === TOUR_STEPS.length - 1;
  const progress = ((currentStep + 1) / TOUR_STEPS.length) * 100;

  const handleNext = () => {
    if (isLast) {
      handleComplete();
    } else {
      const nextStep = TOUR_STEPS[currentStep + 1];
      if (nextStep.page) navigate(nextStep.page);
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrev = () => {
    if (!isFirst) {
      const prevStep = TOUR_STEPS[currentStep - 1];
      if (prevStep.page) navigate(prevStep.page);
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = () => {
    handleComplete();
  };

  const handleComplete = () => {
    setVisible(false);
    localStorage.setItem('lexagenda_onboarding_done', 'true');
    if (onComplete) onComplete();
    navigate('/dashboard');
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-4" data-testid="onboarding-overlay">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden" data-testid="onboarding-modal">
        {/* Progress bar */}
        <div className="h-1 bg-[#E2E6EA]">
          <div
            className="h-full bg-[#00A3A3] transition-all duration-500"
            style={{ width: `${progress}%` }}
          />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-4">
          <span className="text-xs text-[#A8B1B9] font-medium">
            Step {currentStep + 1} of {TOUR_STEPS.length}
          </span>
          <button
            onClick={handleSkip}
            className="text-[#A8B1B9] hover:text-[#5A6570] transition-colors"
            data-testid="onboarding-skip-btn"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-6 text-center">
          <div
            className="w-16 h-16 rounded-2xl mx-auto mb-5 flex items-center justify-center"
            style={{ backgroundColor: `${step.color}15` }}
          >
            <Icon className="h-8 w-8" style={{ color: step.color }} />
          </div>
          <h2 className="text-xl font-bold text-[#1A1D21] mb-3">{step.title}</h2>
          <p className="text-sm text-[#5A6570] leading-relaxed max-w-sm mx-auto">
            {step.description}
          </p>
        </div>

        {/* Step indicators */}
        <div className="flex items-center justify-center gap-1.5 pb-4">
          {TOUR_STEPS.map((_, i) => (
            <div
              key={i}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                i === currentStep ? 'w-6 bg-[#00A3A3]' : i < currentStep ? 'w-1.5 bg-[#00A3A3]/40' : 'w-1.5 bg-[#E2E6EA]'
              }`}
            />
          ))}
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between px-6 pb-6">
          <Button
            variant="ghost"
            onClick={handlePrev}
            disabled={isFirst}
            className="text-[#5A6570]"
            data-testid="onboarding-prev-btn"
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Back
          </Button>
          <div className="flex gap-2">
            {!isLast && (
              <Button
                variant="outline"
                onClick={handleSkip}
                className="text-[#5A6570]"
                data-testid="onboarding-skip-text-btn"
              >
                Skip Tour
              </Button>
            )}
            <Button
              onClick={handleNext}
              className="bg-[#00A3A3] hover:bg-[#008585]"
              data-testid="onboarding-next-btn"
            >
              {isLast ? 'Get Started' : 'Next'}
              {!isLast && <ChevronRight className="h-4 w-4 ml-1" />}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingTutorial;
